import React, { useState } from 'react';
import { Modal, Box, TextField, Button, FormControl, Typography, Grid, Alert, AlertTitle, CircularProgress, IconButton, Paper, Fade } from '@mui/material';
import { API } from 'aws-amplify';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';

const AttendanceForm = ({ open, onClose }) => {
    const [formData, setFormData] = useState({
        email: "",
        startTime: '09:00',
        endTime: '18:00',
        agenda: '',
        date: moment(new Date()).format("YYYY-MM-DD")
    });
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({
        open: false,
        severity: 'success', // 'success' or 'error'
        message: '',
    });
    const handleCloseAlert = () => {
        setAlert({
            ...alert,
            open: false,
        });
        setFormData({
            startTime: '',
            endTime: '',
            agenda: ''
        });
        onClose();
    };
    const handleSuccess = (message) => {
        setAlert({
            open: true,
            severity: 'success',
            message,
        });
        setLoading(false); // Hide loader
    };

    const handleError = (message) => {
        setAlert({
            open: true,
            severity: 'error',
            message,
        });
        setLoading(false); // Hide loader
    };
    function generateUniqueId() {
        const timestamp = new Date().getTime().toString(16); // Convert timestamp to hexadecimal
        const randomPart = Math.random().toString(16).slice(2, 8); // Random hex string

        return timestamp + randomPart;
    }
    const handleSubmit = async (event) => {
        event.preventDefault();
        const currentUser = localStorage.getItem("user");
        const uniqueId = generateUniqueId();
        setLoading(true); // Show loader
        //console.log(FormData);
        let apiName = 'Node-API';
        let path = ''; //replace this with the path you have configured on your API
        let params = {
            body:
            {
                "command": "addAttendance",
                "id": uniqueId,
                "email": currentUser,
                "startTime": formData.startTime,
                "endTime": formData.endTime,
                "agenda": formData.agenda,
                "date": formData.date,
                "status": "true"
            },
            response: true,
            convertEmptyValues: true,
            headers: {}
        }
        //console.log(params)
        API.post(apiName, path, params).then(response => {
            //console.log (response.data);

            if (response.data.statusCode === 200) {
                //console.log(response);
                handleSuccess("Successfully added")
            }
            else {
                //console.log(response);
                handleError('Unable to add. Please try again.');
            }
        }
        ).catch(error => {
            console.log(error);
            handleError('Registration failed. Please try again.');
        });
    };

    return (
        <Modal 
            open={open} 
            onClose={onClose} 
            closeAfterTransition
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Fade in={open}>
                <Paper
                    elevation={8}
                    sx={{
                        width: '100%',
                        maxWidth: 500,
                        borderRadius: '16px',
                        p: 0,
                        maxHeight: '85vh',
                        overflowY: 'auto',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                    }}
                >

                    {/* Header */}
                    <Box sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        p: 2,
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center'
                    }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon />
                            Add Attendance Note
                        </Typography>
                        <IconButton 
                            onClick={onClose}
                            sx={{ 
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 3 }}>
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                <CircularProgress size={60} sx={{ color: '#667eea' }} />
                            </Box>
                        )}
                        
                        {alert.open && (
                            <Alert 
                                severity={alert.severity} 
                                onClose={handleCloseAlert}
                                sx={{ 
                                    mb: 3,
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            >
                                <AlertTitle sx={{ fontWeight: 600 }}>
                                    {alert.severity === 'success' ? 'Success' : 'Error'}
                                </AlertTitle>
                                {alert.message}
                            </Alert>
                        )}
                        
                        {!loading && !alert.open && (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Start Time"
                                            type="time"
                                            value={formData.startTime}
                                            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                                            fullWidth
                                            InputProps={{
                                                startAdornment: <AccessTimeIcon sx={{ color: '#667eea', mr: 1 }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    '&:hover fieldset': {
                                                        borderColor: '#667eea'
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="End Time"
                                            type="time"
                                            value={formData.endTime}
                                            onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                                            fullWidth
                                            InputProps={{
                                                startAdornment: <AccessTimeIcon sx={{ color: '#667eea', mr: 1 }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    '&:hover fieldset': {
                                                        borderColor: '#667eea'
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Work Description"
                                            value={formData.agenda}
                                            onChange={(e) => setFormData({ ...formData, agenda: e.target.value })}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            placeholder="Please add a detailed description of the work you accomplished today..."
                                            InputProps={{
                                                startAdornment: <DescriptionIcon sx={{ color: '#667eea', mr: 1, mt: 1, alignSelf: 'flex-start' }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    '&:hover fieldset': {
                                                        borderColor: '#667eea'
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            p: 2,
                                            backgroundColor: '#f0f9ff',
                                            borderRadius: '12px',
                                            border: '1px solid #e0f2fe'
                                        }}>
                                            <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 500 }}>
                                                💡 Tip: Be specific about your accomplishments, challenges faced, and tasks completed during your work session.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={onClose}
                                        sx={{
                                            textTransform: 'capitalize',
                                            borderColor: '#d1d5db',
                                            color: '#6b7280',
                                            borderRadius: '12px',
                                            px: 3,
                                            py: 1.5,
                                            '&:hover': {
                                                borderColor: '#9ca3af',
                                                backgroundColor: '#f9fafb'
                                            }
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        sx={{
                                            textTransform: 'capitalize',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            borderRadius: '12px',
                                            px: 3,
                                            py: 1.5,
                                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #5a6fd8 0%, #684a94 100%)',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        Save Note
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Box>
                </Paper>
            </Fade>
        </Modal>

    );
};



export default AttendanceForm;
